import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
    CreateSupportCategory,
    DeleteSupportCategory,
    GetSupportCategories,
    UpdateSupportCategory,
} from '../services/backend';
import { Notify, NotifyError } from '../utils/functions';

/**
 * The ticket taxonomy is admin-configurable (SRD §3, §11), so every dropdown, filter
 * and label lookup reads it from here rather than a hardcoded enum.
 *
 * Cached for 5 minutes: it changes rarely but is needed by almost every support screen,
 * and refetching it on each mount would be wasted requests.
 *
 * `includeInactive` is for the settings screen, which has to show retired categories
 * so they can be reactivated. Everywhere else gets active ones only.
 */
export function useGetSupportCategories({ includeInactive = false } = {}) {
    return useQuery(
        ['support-categories', includeInactive],
        () => GetSupportCategories(includeInactive ? { includeInactive: true } : undefined),
        {
            staleTime: 5 * 60 * 1000,
            select: (data) => data?.data?.categories || [],
            onError: (error) => {
                NotifyError('Failed to load support categories', error);
                console.error('Error loading support categories:', error);
            },
        }
    );
}

const invalidate = (queryClient) => queryClient.invalidateQueries('support-categories');

export function useCreateSupportCategory() {
    const queryClient = useQueryClient();
    return useMutation((data) => CreateSupportCategory(data), {
        onSuccess: () => {
            Notify('Category created successfully', 'success');
            invalidate(queryClient);
        },
        onError: (error) => NotifyError('Failed to create category', error),
    });
}

export function useUpdateSupportCategory() {
    const queryClient = useQueryClient();
    return useMutation(({ categoryId, data }) => UpdateSupportCategory(categoryId, data), {
        onSuccess: () => {
            Notify('Category updated successfully', 'success');
            invalidate(queryClient);
        },
        onError: (error) => NotifyError('Failed to update category', error),
    });
}

export function useDeleteSupportCategory() {
    const queryClient = useQueryClient();
    return useMutation((categoryId) => DeleteSupportCategory(categoryId), {
        onSuccess: (result) => {
            // The API deactivates rather than deletes when tickets still reference the
            // category, and says so in the message — surface that instead of claiming
            // it was deleted.
            Notify(result?.data?.message || 'Category removed', 'success');
            invalidate(queryClient);
        },
        onError: (error) => NotifyError('Failed to remove category', error),
    });
}
